import { useCallStore, selectIncomingCall } from '../store/callStore';
import { useCall, tryUnlockRingtoneAudio } from '../hooks/useCall';
import { useVaultStore } from '../store/vaultStore';
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import { callTabCoordination } from '../services/callTabCoordination';
import { isWebViewMode } from '../utils/webviewBridge';

export function IncomingCallModal() {
  if (isWebViewMode) {
    return null;
  }

  return <IncomingCallModalContent />;
}

function IncomingCallModalContent() {
  const incomingCall = useCallStore(selectIncomingCall);
  const { answerCall, rejectCall } = useCall();
  const vaultData = useVaultStore((s) => s.vaultData);
  const { t } = useI18n();
  const [isLeader, setIsLeader] = useState(() => callTabCoordination.getSnapshot(incomingCall?.callSessionId).isLeader);

  useEffect(() => {
    setIsLeader(callTabCoordination.getSnapshot(incomingCall?.callSessionId).isLeader);

    return callTabCoordination.subscribe((event) => {
      if (!incomingCall?.callSessionId) {
        setIsLeader(false);
        return;
      }
      if (event.callSessionId !== incomingCall.callSessionId) return;
      setIsLeader(callTabCoordination.getSnapshot(incomingCall.callSessionId).isLeader);
    });
  }, [incomingCall?.callSessionId]);

  const resolved = useMemo(() => {
    if (!incomingCall || !vaultData) return null;

    const fromId = (incomingCall.fromIdentityId || '').trim();
    const fromKey = (incomingCall.fromIdentityPublicKey?.value || '').trim();

    const contacts = Object.values(vaultData.contacts || {});
    for (const contact of contacts) {
      const has = contact.identities.some((i) => {
        const iId = (i.identityId || '').trim();
        const iKey = (i.identityPublicKey.value || '').trim();

        if (fromKey && iKey && iKey === fromKey) return true;
        if (fromId && iId && iId === fromId) return true;
        return false;
      });
      if (has) return contact;
    }

    return null;
  }, [incomingCall, vaultData]);

  const displayName = resolved?.displayName || incomingCall?.fromDisplayName || t('common.unknown');
  const isKnownContact = !!resolved || !!incomingCall?.isKnownContact;

  if (!incomingCall || !isLeader) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onPointerDown={() => {
        // Browsers may block WebAudio until a user gesture; use the overlay click as the gesture.
        void tryUnlockRingtoneAudio();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm w-full mx-4 animate-bounce-slow">
        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 bg-primary-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-4xl font-bold text-white">
              {incomingCall.fromDisplayName?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>

          {/* Caller name */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {displayName}
          </h2>
          {!isKnownContact && incomingCall.fromIdentityName && (
            <p className="text-[0.625rem] text-gray-500 mb-2">{incomingCall.fromIdentityName}</p>
          )}

          {/* Status */}
          <p className="text-gray-600 mb-2">{t('incomingCall.incoming')}</p>

          {!isKnownContact && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 w-full mb-4">
              <p className="text-sm text-yellow-800 font-medium mb-1">{t('incomingCall.unknownKeyTitle')}</p>
              <p className="text-xs text-yellow-800 break-all">{t('incomingCall.identityId', { id: incomingCall.fromIdentityId })}</p>
              <p className="text-xs text-yellow-800 mt-2">
                {t('incomingCall.unknownKeyHint')}
              </p>
            </div>
          )}

          {/* E2EE Security indicator */}
          {incomingCall.e2eeVerified && (
            <div className="flex items-center justify-center text-green-600 text-sm mb-6">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {t('incomingCall.e2eeVerified')}
            </div>
          )}

          {!incomingCall.e2eeVerified && (
            <div className="flex items-center justify-center text-yellow-600 text-sm mb-6">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {t('incomingCall.e2eeUnverified')}
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3 w-full">
            {/* Answer buttons row */}
            <div className="flex space-x-3">
              {/* Answer with audio button */}
              <button
                onClick={() => answerCall(false)}
                className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {t('incomingCall.audio')}
              </button>

              {/* Answer with video button */}
              <button
                onClick={() => answerCall(true)}
                className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('incomingCall.video')}
              </button>
            </div>

            {/* Decline button */}
            <button
              onClick={rejectCall}
              className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
              </svg>
              {t('incomingCall.decline')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
